import { useAppSelector } from 'dux/app/hooks';

import { Box, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';
import { useGetTrialOfferCategory } from 'hooks/useRedeemCouponForTrialOffer';

import { productsCategories, type productsSlugs } from 'constants/products';

import { formatPriceWithCurrency } from 'utils/currencies';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import {
  getTrialOfferFlagVariant,
  shouldShowTrialOffer,
  trialOfferEvergreen,
} from 'dux/featureFlags/selectors';
import { getCategoryByProductSlug, getProductsPrices } from 'dux/products/selectors';
import { getUserSubscriptionDiscount } from 'dux/user/selectors';
import { useFetchProductsList } from 'dux/products/hooks';

interface ProductPriceModuleProps {
  productSlug: (typeof productsSlugs)[keyof typeof productsSlugs];
}

const Container = styled.div`
  width: 100%;
  padding: ${theme.spacing['4x']} ${theme.spacing['6x']};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.radius.xs};

  background-color: ${theme.colors.neutral[200]};

  ${mediaMinWidth('md')} {
    border: 0.5px solid ${theme.colors.primary[200]};
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 0.5px;
  margin: ${theme.spacing['4x']} 0;
  background-color: ${theme.colors.primary[200]};
`;

const BenefitSeparator = styled.div`
  display: flex;
  align-items: flex-start;
  margin: 0 5px;
`;

export const ProductPriceModule = ({ productSlug }: ProductPriceModuleProps) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });
  // Fetch products list
  useFetchProductsList();
  const productCategory = useAppSelector((state) =>
    getCategoryByProductSlug(state, { productSlug }),
  ) as (typeof productsCategories)[keyof typeof productsCategories];
  const subDiscount: number = useAppSelector(getUserSubscriptionDiscount) * 100;
  const showTrialOffer = useAppSelector(shouldShowTrialOffer);
  // @ts-expect-error dux not typed yet
  const trialOfferVariant = useAppSelector(getTrialOfferFlagVariant);
  const productsPrices: {
    currency: string;
    slug: string;
    regularPrice: number;
    priceWithSubscription: number;
  }[] = useAppSelector(getProductsPrices);

  const eligibleCategoryForTrialOffer: (typeof productsCategories)[keyof typeof productsCategories] =
    useGetTrialOfferCategory();

  const productData = productsPrices?.find((product) => product.slug === productSlug);

  // Wait for products to be fetched
  if (!productData) {
    return null;
  }

  const { regularPrice, currency, priceWithSubscription } = productData;

  // This is a V1, all the logic with prices and messages from offer needs to be moved on api side
  const trialOfferEligibility =
    showTrialOffer &&
    (eligibleCategoryForTrialOffer === productCategory ||
      eligibleCategoryForTrialOffer === productsCategories.AGNOSTIC);

  // Today the trial offer is 60% off
  const priceWithDiscount = trialOfferEligibility ? regularPrice * 0.4 : priceWithSubscription;

  const discountMessage =
    trialOfferEligibility && trialOfferVariant === trialOfferEvergreen
      ? 'for 60% off your first order'
      : trialOfferEligibility
      ? 'for 60% off your first order + a free gift'
      : `for ${subDiscount}% off`;

  return (
    <Container>
      <Box display="flex" flexDirection="row" justifyContent="space-between" width="full">
        <Box width="full">
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Box>
              <Text variant={isMobile ? 'body4medium' : 'body3medium'}>Subscribe</Text>
            </Box>
            <Box display="flex" flexDirection="row">
              <Text
                color="neutral.800"
                paddingRight="3x"
                textDecorationLine="lineThrough"
                variant={isMobile ? 'body4regular' : 'body3regular'}
              >
                {formatPriceWithCurrency({
                  currency,
                  price: regularPrice,
                })}
              </Text>
              <Text variant={isMobile ? 'body4regular' : 'body3regular'}>
                {formatPriceWithCurrency({
                  currency,
                  price: priceWithDiscount,
                })}
              </Text>
            </Box>
          </Box>
          <Text marginBottom="4x" variant={isMobile ? 'body4regular' : 'body3regular'}>
            {discountMessage}
          </Text>
          <Box width="full">
            <Box display="flex" flexDirection="row">
              <Text
                __lineHeight="24px"
                color="neutral.800"
                variant={isMobile ? 'body5regular' : 'body4regular'}
              >
                15% off every future order
              </Text>
              <BenefitSeparator>
                <Text color="neutral.800">•</Text>
              </BenefitSeparator>
              <Text
                __lineHeight="24px"
                color="neutral.800"
                variant={isMobile ? 'body5regular' : 'body4regular'}
              >
                Free shipping, always
              </Text>
            </Box>
            <Box display="flex" flexDirection="row" marginTop="1x">
              <Text
                __lineHeight="24px"
                color="neutral.800"
                variant={isMobile ? 'body5regular' : 'body4regular'}
              >
                Cancel or modify anytime
              </Text>
              <BenefitSeparator>
                <Text color="neutral.800">•</Text>
              </BenefitSeparator>
              <Text
                __lineHeight="24px"
                color="neutral.800"
                variant={isMobile ? 'body5regular' : 'body4regular'}
              >
                Refills on your schedule
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Text variant={isMobile ? 'body4medium' : 'body3medium'}>Buy once</Text>
        <Text variant={isMobile ? 'body4regular' : 'body3regular'}>
          {formatPriceWithCurrency({
            currency,
            price: regularPrice,
          })}
        </Text>
      </Box>
    </Container>
  );
};
